import { useState } from "react";
import { API_BASE } from "./config";
import type { PhotoItem } from "./types";
import { usePhotos } from "./hooks/usePhotos";
import { CompUploadForm } from "./CompUploadForm";
import { CompGalleryCard } from "./CompGalleryCard";
import { CompFullscreenViewer } from "./CompFullscreenViewer";

export function PageGallery() {
  const [fullscreen, setFullscreen] = useState<PhotoItem | null>(null);
  const { photos, loading, error, fetchPhotos, deletePhoto, deletingId } = usePhotos();

  return (
    <div className="relative z-10 min-h-screen py-8 px-4 sm:py-10 sm:px-6">
      <CompFullscreenViewer
        photo={fullscreen}
        photos={photos}
        onPhotoChange={setFullscreen}
        onClose={() => setFullscreen(null)}
      />
      <div className="w-full max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <header className="text-center space-y-2 animate-[fade-in_0.5s_ease-out]">
          <p className="text-[var(--wedding-ink-muted)] text-sm uppercase tracking-[0.2em] font-medium">
            Gästefotos & Videos
          </p>
          <h1 className="font-wedding-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-[var(--wedding-ink)]">
            Birgit & Philipp
          </h1>
          <p className="text-[var(--wedding-ink-muted)] text-base sm:text-lg max-w-md mx-auto">
            Teilt eure schönsten Momente vom großen Tag — Fotos und Videos landen hier in der gemeinsamen Galerie.
          </p>
        </header>

        {!loading && !error && photos.length === 0 && (
          <div className="text-center py-4 px-4 rounded-2xl bg-[var(--wedding-white)]/80 border border-[var(--wedding-blush-strong)] shadow-sm max-w-lg mx-auto animate-[fade-in_0.5s_ease-out_0.1s_both]">
            <p className="text-[var(--wedding-ink-muted)] text-sm sm:text-base leading-relaxed">
              Noch keine Fotos in der Galerie. Legt Dateien auf die Karte unten ab oder tippt darauf, um eure Fotos und Videos hinzuzufügen.
            </p>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center gap-3 py-8">
            <div className="w-8 h-8 border-2 border-[var(--wedding-gold)] border-t-transparent rounded-full animate-spin" />
            <p className="text-[var(--wedding-ink-muted)]">Fotos werden geladen…</p>
          </div>
        )}
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3" role="alert">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {!loading && (
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-5 list-none p-0 m-0">
            <CompUploadForm onUploaded={() => fetchPhotos(true)} />
            {photos.map((p) => (
              <CompGalleryCard
                key={p.id}
                photo={p}
                apiBase={API_BASE}
                onFullscreen={setFullscreen}
                onDelete={deletePhoto}
                isDeleting={deletingId === p.id}
              />
            ))}
            {!error && photos.length === 0 &&
              [1, 2, 3, 4, 5].map((i) => (
                <li key={`placeholder-${i}`} className="aspect-square" aria-hidden />
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}
